import { Card, Input, Skeleton, Modal } from "antd";
import { pathOr, equals } from "ramda";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { setPopup } from "../../../../actions/popupAction";
import CustomButton from "../../../../components/customButton";
import CustomLabel from "../../../../components/customLabel";
import { CustomSimpleSelect } from "../../../../components/customSelect";
import { MultipleFormikSelect } from "../../../../components/select/multipleSelect";
import BeePage from "../../../../components/emailEditor/beefree/bee_page";
import emailEditorService from "../../../../service/emailEditorService";

import "./createStyles.scss";
import { debounceAsync } from "../../../../helper/helpers";
import { DEBOUNCE_TIME } from "../../../../constants/constants";
import { ERRORS } from "../../constants";

const CreateEmailTemplate = ({
  templateId,
  practiceId,
  onSave: onSaveTemplate,
  onUpdate,
  onBack,
  selectedSample,
  sampleList,
  mergeTags,
  isTemplateModificationAllowed,
  fetchAccounts,
  accountsList = [],
  allAccountsList = [],
  formValues,
  setFieldValue,
}) => {
  const dispatch = useDispatch();
  const [state, setState] = useState({
    name: "",
    newName: "",
    error: "",
    template: undefined,
    selectedSample: selectedSample || "sample1",
    pluginLoaded: false,
    templateLoaded: false,
    saving: false,
    beePlugin: undefined,
    ifSaveAsClicked: false,
    isModalOpen: false,
    inPreviewMode: false,
    saveAsNameTouched: false,
    saveAsError: "",
    showAs: "custom",
    isDefaultTemplate: false,
  });

  const nameRef = useRef();
  nameRef.current = state.name;

  const showAsRef = useRef();
  showAsRef.current = state.showAs;

  const formValuesRef = useRef();
  formValuesRef.current = formValues;

  const saveAsRef = useRef();
  saveAsRef.current = state.ifSaveAsClicked;

  const addNewTemplate = (jsonData, htmlData) => {
    const name = nameRef.current;
    const showAs = showAsRef.current;
    const accounts = formValuesRef.current?.accounts;

    const accountIdsSelected = isTemplateModificationAllowed
      ? accounts.map((value) => value.practiceId || "all")
      : null;

    emailEditorService
      .addEmailTemplate({
        name,
        ...(isTemplateModificationAllowed
          ? { accounts: accountIdsSelected }
          : {}),
        jsonData,
        htmlData,
        showAs: showAs,
      })
      .then(({ data }) => {
        const obj = data.data;
        onSaveTemplate(obj);
        setState((st) => ({ ...st, ifSaveAsClicked: false }));
        dispatch(
          setPopup({
            customPopupMessage: true,
            messageType: "success",
            message: "Email template has been created successfully.",
          })
        );
      })
      .catch((err) => {
        dispatch(
          setPopup({
            customPopupMessage: true,
            messageType: "error",
            message: pathOr(
              "Some error occured",
              ["response", "data", "message"],
              err
            ),
            currentActionItemName: name,
          })
        );
        setState((st) => ({ ...st, saving: false }));
      });
  };

  const editEmailTemplate = (jsonData, htmlData) => {
    const { template } = state;
    const name = nameRef.current;

    const accountIdsSelected = isTemplateModificationAllowed
      ? formValuesRef.current?.accounts?.map(
          (value) => value.practiceId || "all"
        )
      : null;

    emailEditorService
      .updateEmailTemplate({
        id: template.id,
        name,
        jsonData,
        htmlData,
        ...(isTemplateModificationAllowed
          ? { accounts: accountIdsSelected }
          : {}),
      })
      .then(() => {
        onUpdate();
        dispatch(
          setPopup({
            customPopupMessage: true,
            messageType: "success",
            message: "Email template has been updated successfully.",
          })
        );
      })
      .catch((err) => {
        dispatch(
          setPopup({
            customPopupMessage: true,
            messageType: "error",
            message: pathOr(
              "Some error occured",
              ["response", "data", "message"],
              err
            ),
            currentActionItemName: name,
          })
        );
        setState((st) => ({ ...st, saving: false }));
      });
  };

  const onSave = (jsonData, htmlData) => {
    const name = nameRef.current;
    const ifSaveAsClicked = saveAsRef.current;
    if (name) {
      setState((st) => ({ ...st, saving: true }));
      if (!templateId || ifSaveAsClicked) {
        addNewTemplate(jsonData, htmlData);
      } else {
        editEmailTemplate(jsonData, htmlData);
      }
    } else {
      dispatch(
        setPopup({
          customPopupMessage: true,
          messageType: "error",
          message: "Please provide a name for the template",
          currentActionItemName: name,
        })
      );
      setState((st) => ({ ...st, error: "Name is required" }));
    }
  };

  useEffect(() => {
    if (templateId) {
      emailEditorService
        .getEmailEditorById(templateId)
        .then((data) => {
          const template = data.data[0];
          let parsedData;
          try {
            parsedData = JSON.parse(template.jsonData);
          } catch {
            parsedData = JSON.parse("");
          }
          setState((st) => ({
            ...st,
            template: {
              ...template,
              jsonData: parsedData,
            },
            name: template.name,
            newName: template.name,
            templateLoaded: true,
            isDefaultTemplate: template.showAs === "default",
          }));

          const selectedTemplateAccounts = allAccountsList.filter((val) =>
            template.accounts.includes(val.practiceId)
          );

          if (template.accounts[0] === "all") {
            selectedTemplateAccounts.push({ id: "all", name: "All" });
          }
          setFieldValue("accounts", selectedTemplateAccounts);
        })
        .catch((err) => {});
    } else {
      import(`../../../../components/emailEditor/beefree/${
        state.selectedSample
      }.json`)
        .then((template) => {
          setState((st) => ({
            ...st,
            template: { jsonData: template.default },
            templateLoaded: true,
            isDefaultTemplate: isTemplateModificationAllowed ? true : false,
          }));
        })
        .catch((err) => console.error(err));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(
    () => {
      if (template?.accounts) {
        const selectedTemplateAccounts = allAccountsList.filter((val) =>
          template.accounts.includes(val.practiceId)
        );

        if (template.accounts[0] === "all") {
          selectedTemplateAccounts.push({ id: "all", name: "All" });
        }
        setFieldValue("accounts", selectedTemplateAccounts);
      }
    },
    [allAccountsList?.length]
  );

  const onTogglePreview = (inPreviewMode) => {
    setState((st) => ({ ...st, inPreviewMode }));
  };

  const {
    beePlugin,
    template,
    pluginLoaded,
    templateLoaded,
    saving,
    isModalOpen,
    name,
    newName,
  } = state;

  const setBeePluginAPI = (beePlugin) => {
    setState((st) => ({ ...st, beePlugin }));
  };

  const handleSave = (type = "save") => {
    if (templateId && beePlugin && type === "save") {
      if (isTemplateModificationAllowed) {
        setState((st) => ({
          ...st,
          isModalOpen: true,
          saveAsNameTouched: false,
        }));
      } else {
        beePlugin.save();
      }
    } else {
      setState((st) => ({
        ...st,
        ifSaveAsClicked: true,
        isModalOpen: true,
        saveAsNameTouched: false,
      }));
    }
  };

  const handleModalOk = () => {
    if (beePlugin) {
      if (saveAsRef.current) {
        beePlugin.save();
      } else {
        beePlugin.save();
      }
    }
    setState((st) => ({
      ...st,
      isModalOpen: false,
      name: st.newName,
      error: "",
    }));
  };

  const handleModalCancel = () => {
    setState((st) => ({
      ...st,
      ifSaveAsClicked: false,
      isModalOpen: false,
      newName: st.name,
    }));
  };

  const isSaveDisabled =
    !name ||
    (!isTemplateModificationAllowed && state.isDefaultTemplate) ||
    (!state.isDefaultTemplate && isTemplateModificationAllowed);
  const saveButtonClassName = isSaveDisabled ? "disable-button" : "";

  const accoundIds = formValuesRef?.current?.accounts
    ? formValuesRef?.current?.accounts?.map(
        (value) => value.practiceId || "All"
      )
    : [];

  const accountsNotSelected =
    isTemplateModificationAllowed &&
    (templateId && !saveAsRef.current
      ? equals(accoundIds.sort(), template?.accounts.sort()) ||
        !accoundIds.length
      : !formValuesRef.current?.accounts?.length);

  const nameNotChanged = template?.name === newName.trim();

  const isOkButtonDisabled =
    !newName ||
    (saveAsRef.current
      ? nameNotChanged || accountsNotSelected
      : nameNotChanged && accountsNotSelected) ||
    state.saveAsError;
  const okButtonClassName = isOkButtonDisabled ? "disable-button" : "";

  const debouncedCheckTemplateExists = useCallback(
    debounceAsync(
      emailEditorService.checkEmailTemplateExists,
      DEBOUNCE_TIME,
      false
    ),
    [emailEditorService.checkEmailTemplateExists]
  );

  useEffect(
    () => {
      if (
        !state.newName.trim() ||
        (isTemplateModificationAllowed &&
          !formValuesRef.current?.accounts?.length)
      ) {
        return;
      }

      const accountIdsSelected = isTemplateModificationAllowed
        ? formValuesRef.current?.accounts?.map(
            (value) => value.practiceId || "all"
          )
        : [];

      debouncedCheckTemplateExists(state.newName, accountIdsSelected)
        .then((resp) => {
          const templateExists = resp.data.exists;
          setState((st) => ({
            ...st,
            saveAsError:
              (templateExists && templateId && state.newName !== state.name) ||
              (templateExists && !templateId)
                ? ERRORS.TEMPLATE.NAME_EXISTS
                : "",
          }));
        })
        .catch((err) => {
          console.error(err);
          setState((st) => ({
            ...st,
            saveAsError: ERRORS.TEMPLATE.CHECK_NAME_API_ERROR,
          }));
        });
    },
    [state.newName, formValuesRef.current?.accounts]
  );
  return (
    <>
      <Modal
        title="Save New Template"
        className="custom-modal email-template-modal"
        mask={false}
        visible={isModalOpen}
        onCancel={handleModalCancel}
        okText="Save As"
        onOk={handleModalOk}
        okButtonProps={{
          disabled: isOkButtonDisabled,
          className: okButtonClassName,
        }}
      >
        <div className="form-row">
          <CustomLabel
            required
            title={`Provide a ${
              template?.practiceId === "default" ? "new" : ""
            } name for your template`}
            customClass="name-field-label"
          />
          <Input
            className={`custom-form-input ${
              state.saveAsError ? "error-field" : ""
            }`}
            type="text"
            placeholder="Name"
            onChange={(event) => {
              const newName = event.target.value;
              setState((st) => ({
                ...st,
                newName,
              }));
            }}
            value={state.newName}
            onBlur={() => {
              setState((st) => ({
                ...st,
                saveAsNameTouched: true,
              }));
            }}
          />
          {state.saveAsError && (
            <div className="error-label">{state.saveAsError}</div>
          )}
        </div>
        {isTemplateModificationAllowed && (
          <div className="form-row">
            <MultipleFormikSelect
              data={accountsList}
              name="accounts"
              optionLabelProp="name"
              optionValueProp="id"
              placeholder="Select account name"
              required
              title="Add to Accounts"
              infiniteScroll
              showAll={true}
              loadMoreData={fetchAccounts}
            />
          </div>
        )}
      </Modal>
      <div className="create-email-template">
        <Card className="card">
          <div className="email-template-header">
            <section className="header-content">
              <strong className="heading">Create Email Template</strong>
            </section>

            {!saving ? (
              <div className="buttons-container">
                <CustomButton type="link" onClick={onBack} title="Go Back" />
                <div className="vertical-divider" />
                <CustomButton
                  title="Save"
                  type="primary"
                  disabled={isSaveDisabled}
                  className={saveButtonClassName}
                  onClick={() => handleSave("save")}
                />
                <CustomButton
                  className="cancel-button"
                  title="Save As"
                  onClick={() => handleSave("saveAs")}
                  disabled={
                    !templateId ||
                    (!isTemplateModificationAllowed &&
                      state.isDefaultTemplate) ||
                    (!state.isDefaultTemplate && isTemplateModificationAllowed)
                  }
                />
              </div>
            ) : (
              <div className="saving-popup">Saving in process</div>
            )}
          </div>

          <div className="email-template-fields-wrapper">
            <div className="template-fields">
              <div className="template-name-field">
                <CustomLabel
                  required
                  title="Provide a name for your template"
                  customClass="name-field-label"
                />
                <Input
                  className={`custom-form-input ${
                    state.error ? "error-field" : ""
                  }`}
                  type="text"
                  placeholder="Name"
                  onChange={(event) => {
                    const name = event.target.value;
                    setState((st) => ({
                      ...st,
                      name,
                      newName: name,
                      error: name ? "" : "Name is required",
                    }));
                  }}
                  value={state.name}
                  disabled={
                    (!isTemplateModificationAllowed &&
                      state.isDefaultTemplate) ||
                    (!state.isDefaultTemplate && isTemplateModificationAllowed)
                  }
                />
                {state.error && (
                  <div className="error-label">{state.error}</div>
                )}
              </div>
              {!templateId &&
                !state.inPreviewMode && (
                  <div className="sample-dropdown">
                    <CustomSimpleSelect
                      style={{ width: 126 }}
                      className="common-select-field"
                      disabled={!pluginLoaded}
                      placeholder="Select Template"
                      value={state.selectedSample}
                      onChange={(value) => {
                        setState((st) => ({ ...st, selectedSample: value }));
                        import(`../../../../components/emailEditor/beefree/${value}.json`)
                          .then((sampleTemplate) => {
                            setState((st) => ({
                              ...st,
                              template: { jsonData: sampleTemplate.default },
                            }));
                          })
                          .catch((err) => console.error(err));
                      }}
                      options={
                        sampleList || [{ label: "Sample 1", value: "sample1" }]
                      }
                    />
                  </div>
                )}
            </div>
          </div>

          <div className="editor-plugin">
            <div className="loading-overlay">
              <Skeleton loading active paragraph={{ rows: 6 }} />
            </div>
            {templateLoaded && (
              <BeePage
                template={template && template.jsonData}
                onSave={(jsonFile, htmlFile) => {
                  onSave(jsonFile, htmlFile);
                }}
                onStart={() =>
                  setState((st) => ({ ...st, pluginLoaded: true }))
                }
                practiceId={practiceId}
                setBeePluginAPI={setBeePluginAPI}
                onTogglePreview={onTogglePreview}
                mergeTags={mergeTags}
              />
            )}
          </div>
        </Card>
      </div>
    </>
  );
};

export default CreateEmailTemplate;
